// Static board art baked into a few chunk textures, the way tilemap engines cache a static layer.
// A crawl's ground is thousands of tile images (several per square, plus the terraces' vectors);
// drawn one by one, every frame re-batches and re-uploads all of them though none ever moves.
// Baked, the same pixels are a handful of quads. Everything is stamped at whole art pixels and
// scale 1 into textures sampled NEAREST, so a chunk shows exactly what the images showed.
// A chunk holding two-frame animations (water, fire, dense bushes) is baked once per frame and
// swaps textures as the tiles would have swapped frames. A lost WebGL context loses every baked
// texture, so the last bake is kept and baked again when the context comes back.
import Phaser from 'phaser';

/** One image to bake: a frame at a board-px position (top-left), and its other animation frame. */
export interface Stamp { key: string; frame: string; x: number; y: number; alt?: { key: string; frame: string } }

/** Chunk side (board px): a crawl's ground is a few dozen; the camera culls the ones out of view. */
export const CHUNK = 512;

interface Chunk { x: number; y: number; w: number; h: number; img: Phaser.GameObjects.Image; tex: Phaser.Textures.DynamicTexture[] }

let serial = 0;

export class BakedChunks {
  private chunks: Chunk[] = [];
  private last?: { w: number; h: number; stamps: readonly Stamp[]; over: readonly Phaser.GameObjects.GameObject[]; overHolds?: (x: number, y: number, w: number, h: number) => boolean };
  private phase = 0;
  private readonly id = `fk-bake-${serial++}`;
  private readonly onRestore = () => { if (this.last) this.bake(this.last.w, this.last.h, this.last.stamps, this.last.over, this.last.overHolds); };

  constructor(private readonly scene: Phaser.Scene, private readonly layer: Phaser.GameObjects.Layer, private readonly size = CHUNK) {
    scene.game.renderer.on(Phaser.Renderer.Events.RESTORE_WEBGL, this.onRestore);
    scene.events.on(Phaser.Scenes.Events.PRE_RENDER, this.cull);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  /**
   * docs/147 PG9: only the chunks the camera shows are drawn. Every chunk went to the batch, so a
   * crawl's ground was batches of sixteen textures, drawn with Phaser's sixteen-texture shader (a
   * choice among sixteen samplers for every pixel of the ground); the five or six in view make a
   * batch of their own. Chunks out of view drew no pixel. Under a shaking or turned camera all draw.
   */
  private readonly cull = (): void => {
    const cam = this.scene.cameras?.main;
    if (!cam) return;
    const all = cam.shakeEffect.isRunning || (cam as unknown as { rotation: number }).rotation !== 0;
    const ox = cam.width * cam.originX, oy = cam.height * cam.originY, m = 2 / cam.zoomX;
    const x0 = cam.scrollX + ox - ox / cam.zoomX - m, y0 = cam.scrollY + oy - oy / cam.zoomY - m;
    const x1 = x0 + cam.width / cam.zoomX + 2 * m, y1 = y0 + cam.height / cam.zoomY + 2 * m;
    for (const ch of this.chunks) ch.img.setVisible(all || (ch.x < x1 && ch.x + ch.w > x0 && ch.y < y1 && ch.y + ch.h > y0));
  };

  /** Chunk images in the layer (tests and the census). */
  get count(): number { return this.chunks.length; }

  /**
   * Bake `stamps` (in draw order), then `over` (game objects in board px, e.g. a Graphics of
   * vectors on whole art pixels), into chunks covering a `w`×`h` board-px area. The chunk images
   * take the place in the layer the stamps would have had. `over` objects stay owned by the caller
   * (keep them out of the display list).
   */
  bake(w: number, h: number, stamps: readonly Stamp[], over: readonly Phaser.GameObjects.GameObject[] = [], overHolds?: (x: number, y: number, w: number, h: number) => boolean): void {
    this.clear();
    this.last = { w, h, stamps, over, ...(overHolds ? { overHolds } : {}) };
    const s = this.size;
    const cols = Math.ceil(w / s), rows = Math.ceil(h / s);
    // each stamp in every chunk its frame overlaps (a tall prop may reach into the chunk above)
    const into: Array<Stamp[]> = Array.from({ length: cols * rows }, () => []);
    const anim = new Uint8Array(cols * rows);
    const tex = this.scene.textures;
    for (const st of stamps) {
      const f = tex.getFrame(st.key, st.frame);
      if (!f) continue;
      const fw = f.cutWidth, fh = f.cutHeight;
      const c0 = Math.max(0, Math.floor(st.x / s)), c1 = Math.min(cols - 1, Math.floor((st.x + fw - 1) / s));
      const r0 = Math.max(0, Math.floor(st.y / s)), r1 = Math.min(rows - 1, Math.floor((st.y + fh - 1) / s));
      for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) { into[r * cols + c]!.push(st); if (st.alt) anim[r * cols + c] = 1; }
    }
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c, x = c * s, y = r * s, cw = Math.min(s, w - x), ch = Math.min(s, h - y);
        const list = into[i]!;
        // `overHolds`: whether the objects drawn over draw anything in this chunk (no chunk for nothing)
        if (!list.length && (!over.length || (overHolds && !overHolds(x, y, cw, ch)))) continue;
        const phases = anim[i] ? 2 : 1;
        const textures: Phaser.Textures.DynamicTexture[] = [];
        for (let p = 0; p < phases; p++) {
          const dt = tex.addDynamicTexture(`${this.id}-${i}-${p}`, cw, ch, false);
          if (!dt) continue;
          for (const st of list) {
            const use = p === 1 && st.alt ? st.alt : st;
            dt.stamp(use.key, use.frame, st.x - x, st.y - y, { originX: 0, originY: 0 });
          }
          for (const o of over) dt.draw(o, -x, -y);
          dt.render();
          textures.push(dt);
        }
        if (!textures.length) continue;
        const img = this.scene.add.image(x, y, textures[0]!).setOrigin(0, 0);
        this.layer.add(img);
        this.chunks.push({ x, y, w: cw, h: ch, img, tex: textures });
      }
    }
    this.setPhase(0);
  }

  /** Show every animated chunk at frame `phase` (0: the stamps' own frames, 1: their `alt`). */
  setPhase(phase: number): void {
    this.phase = phase;
    for (const ch of this.chunks) if (ch.tex.length > 1) ch.img.setTexture(ch.tex[phase % ch.tex.length]!.key);
  }

  /** Whether any chunk animates (the caller's timer has nothing to do otherwise). */
  get animated(): boolean { return this.chunks.some((c) => c.tex.length > 1); }

  clear(): void {
    for (const ch of this.chunks) {
      ch.img.destroy();
      for (const t of ch.tex) t.destroy();
    }
    this.chunks = [];
    void this.phase;
  }

  destroy(): void {
    this.clear();
    this.last = undefined;
    this.scene.game.renderer?.off(Phaser.Renderer.Events.RESTORE_WEBGL, this.onRestore);
    this.scene.events?.off(Phaser.Scenes.Events.PRE_RENDER, this.cull);
  }
}
